import { useEffect, useState } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import isObject from 'lodash/isObject';
import pull from 'lodash/pull';

const observable = makeObservable();
const processed = {};
const explainEnabled = localStorage.getItem('explainEnabled');
Object.assign(window, {
	explainTheme: () => {
		if (!explainEnabled) {
			if (window.confirm('This is a one-time confirmation. Do you want to enable explain mode?') === true) {
				localStorage.setItem('explainEnabled', true);
				window.alert('Done. Please run `explainTheme()` again after page reload');
				window.location.reload();
			}
		}
		observable.next();
	}
});

/**
 * Adding early return because explain is an internal tool,
 * and it should not affect application in any way.
 * `explainEnabled` is not changing it's state, so disabling rules of hooks should not be a problem */
/* eslint-disable react-hooks/rules-of-hooks */
export const useExplain = (value, keyPath, getKey) => {
	if (!explainEnabled) return undefined;
	const [explainedValue, setExplainedValue] = useState();
	useEffect(() => {
		const unsubscribe = observable.subscribe(async () => {
			const nextValue = await replaceImages(cloneDeep(value), keyPath, getKey);
			setExplainedValue(nextValue);
			const tooltip = createTooltip();
			window.onmouseover = (evt) => {
				if (evt.target.tagName === 'IMG') {
					const isDataUrl = evt.target.src.startsWith('data:image');
					const themeImage = isDataUrl && Object.values(processed).find(value => value.dataURL === evt.target.src);
					if (themeImage) {
						console.log(`%c Theme path: ${themeImage.keyPath}`, 'background: black; color: white');
						tooltip.show(themeImage.keyPath);
						evt.target.onmouseout = () => {
							tooltip.hide();
						};
					}
				}
			}
		});
		return () => {
			unsubscribe();
		};
	}, [value]);

	return explainedValue;
};

function createTooltip() {
	let tooltip = document.querySelector('.explain_tooltip');
	if (!tooltip) {
		tooltip = document.createElement('div');
		tooltip.classList.add('explain_tooltip');
		tooltip.style.position = 'absolute';
		tooltip.style.background = 'white';
		tooltip.style.padding = '10px';
		tooltip.style.border = '1px solid black';
		tooltip.style.zIndex = 999999999;
		document.body.appendChild(tooltip);
	}
	let timeout;
	const hide = () => {
		timeout = setTimeout(() => {
			tooltip.style.visibility = 'hidden';
		}, 500);
	};
	const show = (text) => {
		clearTimeout(timeout);
		tooltip.textContent = text;
		tooltip.style.visibility = 'visible';
		const bounds = tooltip.getBoundingClientRect();
		const overflow = (bounds.width + window.event.clientX) - window.innerWidth;
		tooltip.style.left = `${window.event.clientX - (overflow > 0 ? overflow : 0)}px`;
		tooltip.style.top = `${window.event.clientY}px`;
	};

	return {
		hide,
		show,
	}
}

function isHttpUrl(string) {
	try {
		const url = new URL(string);
		return url.protocol === "http:" || url.protocol === "https:";
	} catch (_) {
		return false;
	}
}

function isImage(v) {
	if (typeof v === 'string') {
		const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.tiff', '.tif', '.svg', '.webp'];
		if (!isHttpUrl(v)) return false;
		return v.includes('/media/') || imageExtensions.some(ext => v.endsWith(ext));
	}
	return false;
}

function makeCanvas(width, height) {
	const canvas = document.createElement('canvas');
	const ctx = canvas.getContext('2d');
	canvas.width = width;
	canvas.height = height;
	return { ctx, canvas };
}

function imageCanvas(img) {
	const { ctx, canvas } = makeCanvas(img.width, img.height);
	ctx.drawImage(img, 0, 0);
	return { ctx, canvas };
}

function drawText(text, { canvas, ctx }) {
	const maxFontSize = Math.min(canvas.width, canvas.height) * 0.9;
	let fontSize = 1;
	const maxIterations = 1000;
	let iterations = 0;
	const maxWidth = Math.min(canvas.width, window.innerWidth);
	while (ctx.measureText(text).width < maxWidth * 0.9 && fontSize < maxFontSize && iterations < maxIterations) {
		ctx.font = `${fontSize}px Arial`;
		fontSize++;
		iterations++;
	}
	ctx.font = `${(fontSize - 2)}px Arial`;

	const textX = canvas.width / 2;
	const textY = canvas.height / 2;

	ctx.fillStyle = 'rgba(0, 0, 0, 0.9)';
	const rectHeight = Math.max(canvas.height / 4, fontSize * 2);
	const rectY = (canvas.height - rectHeight) / 2;
	ctx.fillRect(0, rectY, canvas.width, rectHeight);

	ctx.fillStyle = 'white';
	ctx.textAlign = 'center';
	ctx.fillText(text, textX, textY);
	return canvas.toDataURL('image/png');
}

const drawImageSrc = (imageUrl, keyPath) => {
	const imageKey = `${imageUrl}/${keyPath[keyPath.length - 1]}`;
	const setProcessed = (dataURL) => {
		processed[imageKey].dataURL = dataURL;
		processed[imageKey].imageUrl = imageUrl;
		processed[imageKey].keyPath = keyPath;
	};
	const promise = new Promise((res) => {
		try {
			const img = new Image();
			img.onload = () => {
				try {
					img.style.width = Math.min(img.width, window.innerWidth);
					const dataURL = drawText(keyPath, imageCanvas(img));
					setProcessed(dataURL);
					res(dataURL);
				} catch (e) {
					console.log('dataURL fail', e);
					res(imageUrl);
				}
			};
			img.crossOrigin = 'anonymous';
			img.src = imageUrl;
			img.onerror = () => {
				try {
					const { canvas, ctx } = makeCanvas(100, 50);
					const dataURL = drawText(keyPath, { canvas, ctx });
					setProcessed(dataURL);
					res(dataURL);
				} catch (e) {
					console.log('error', imageUrl);
					res(imageUrl);
				}
			}
		} catch (e) {
			console.log('failed to draw on:', imageUrl, 'key path:', keyPath);
			res(imageUrl);
		}
	});
	processed[imageKey] = processed[imageKey] || {
		promise,
	};
	return processed[imageKey].promise;
};

async function replaceImages (obj, keyPath = [], getKey = null) {
	await Promise.all(Object.keys(obj).map(async (key) => {
		if (isObject(obj[key])) {
			obj[key] = await replaceImages(obj[key], [...keyPath, getKey ? getKey(key) : key]);
		}
		if (isImage(obj[key])) {
			obj[key] = await drawImageSrc(obj[key], [...keyPath, key].join(':'));
		}
	}));
	return obj;
}

function makeObservable() {
	const queue = [];
	const subscribe = (fn) => {
		queue.push(fn);
		return () => pull(queue, fn);
	}
	const next = () => {
		queue.forEach(fn => fn());
	};
	return { subscribe, next };
}